import React, { useEffect, useState } from "react"
import { Button, Card, CardBody, CardTitle, Col, Row } from "reactstrap"
import { Form, ErrorMessage, Field, Formik } from "lib/formik"
import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { showAlert } from "store/reducers/alertReducer"
import Spinners from "components/Shared/Spinners"
import Select from "components/Shared/TranslatedSelect"
import { getCountryList } from "store/actions/countriesAction"
import { getAllRegionList } from "store/actions/regionAction"
import { addCitySchema } from "components/Common/validation"
import { addCity, getCityDetails, updateCity } from "store/actions/cityAction"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"

const initialValues = {
  name: "",
  nameAr: "",
  country: "",
  region: "",
  status: "",
  displayOrder: "",
}

const AddCity = ({ mode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = `${
    mode === "add" ? t("Add") : t("Edit")
  } ${"City "}`
  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")

  const { loading, isLoading } = useSelector(state => state.City)
  const { countryList } = useSelector(state => state.Countries)
  const { regionList } = useSelector(state => state.Region)
  const { cityDetails } = useSelector(state => state.City)
  const [formValues, setFormValues] = useState(initialValues)

  const { userRole, accountType } = useSelector(state => state.Login)

  useEffect(() => {
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      26,
      "city_2",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      26,
      "city_3",
      accountType
    )
    if (mode === "add") {
      if (!isPermissionCreate) {
        navigate("/dashboard")
      }
    } else if (mode === "edit") {
      if (!isPermissionEdit) {
        navigate("/dashboard")
      }
    }
  }, [])

  useEffect(() => {
    fetchCountryList()
  }, [])

  useEffect(() => {
    if (mode === "edit") {
      fetchCityDetails()
    }
  }, [])

  const fetchCityDetails = () => {
    const payload = { id }
    dispatch(getCityDetails(payload))
  }

  useEffect(() => {
    if (cityDetails?._id && !loading && mode === "edit") {
      const { name, nameAr, country, region, status, displayOrder } =
        cityDetails
      const updatedCountry = country
        ? {
            label: language === "sa" ? country?.nameAr : country?.name,
            value: country?._id,
          }
        : ""
      const updatedRegion = region
        ? {
            label: language === "sa" ? region?.nameAr : region?.name,
            value: region?._id,
          }
        : ""
      setFormValues({
        name: name || "",
        nameAr: nameAr || "",
        country: updatedCountry,
        region: updatedRegion,
        status,
        displayOrder: displayOrder ?? "",
      })
      if (country?._id) {
        dispatch(getAllRegionList(country._id))
      }
    }
  }, [cityDetails, loading, mode, language, dispatch])

  const fetchCountryList = () => {
    dispatch(getCountryList())
  }

  const fetchRegionList = countryId => {
    dispatch(getAllRegionList(countryId))
  }

  const handleOnSubmit = values => {
    // Keep values if the form remounts behind the save spinner.
    setFormValues(values)
    const { name, nameAr, country, region, status, displayOrder } = values

    const countryId = country?.value
    const regionId = region?.value
    const payload = {
      name,
      nameAr,
      country: countryId,
      region: regionId,
      displayOrder,
    }
    if (mode === "add") {
      dispatch(addCity(payload))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(`/cities`)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    } else if (mode === "edit") {
      const editPayload = {
        ...payload,
        id,
        status: Number(status),
      }
      dispatch(updateCity(editPayload))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(-1)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    }
  }

  if ((mode === "edit" && loading) || isLoading) return <Spinners />

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <CardBody>
                <CardTitle className="mb-2 d-flex align-items-center">
                  <BackButton />
                  {mode === "add" ? t("Add") : t("Edit")} {t("City")}
                </CardTitle>
                <Formik
                  initialValues={formValues}
                  enableReinitialize={true}
                  validationSchema={addCitySchema}
                  onSubmit={handleOnSubmit}
                >
                  {({ values, setFieldValue, errors, touched }) => (
                    <Form>
                      <div className="row">
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="name">
                              <span className="text-danger">*</span>{" "}
                              {t("Name(English)")}
                            </label>
                            <Field
                              type="text"
                              id="name"
                              name="name"
                              placeholder={t("Name(English)")}
                              className={`form-control ${
                                touched.name && errors.name ? "is-invalid" : ""
                              }`}
                            />
                            <TextError name="name" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="name">
                              <span className="text-danger">*</span>{" "}
                              {t("Name(عربي)")}
                            </label>
                            <Field
                              type="text"
                              id="nameAr"
                              name="nameAr"
                              placeholder={t("Name(عربي)")}
                              className={`form-control ${
                                touched.nameAr && errors.nameAr
                                  ? "is-invalid"
                                  : ""
                              }`}
                              style={{ direction: "rtl" }}
                            />
                            <TextError name="nameAr" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="">
                            <label htmlFor="country">
                              <span className="text-danger">*</span>{" "}
                              {t("Select Country")}
                            </label>
                            <Select
                              value={values.country}
                              onChange={item => {
                                setFieldValue("country", item)
                                setFieldValue("region", "")
                                fetchRegionList(item.value)
                              }}
                              options={countryList?.map(item => ({
                                label:
                                  language === "sa" ? item.nameAr : item.name,
                                value: item._id,
                              }))}
                              className={`select2-selection ${
                                touched.country && errors.country
                                  ? "danger_color_border"
                                  : ""
                              } `}
                              placeholder={t("Select Country")}
                            />
                            <TextError name="country" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="">
                            <label htmlFor="region">
                              <span className="text-danger">*</span>{" "}
                              {t("Select Region")}
                            </label>
                            <Select
                              value={values.region}
                              onChange={item => {
                                setFieldValue("region", item)
                              }}
                              options={regionList?.map(item => ({
                                label:
                                  language === "sa" ? item.nameAr : item.name,
                                value: item._id,
                              }))}
                              className={`select2-selection ${
                                touched.region && errors.region
                                  ? "danger_color_border"
                                  : ""
                              } `}
                              placeholder={t("Select Region")}
                            />
                            <TextError name="region" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="displayOrder">
                              <span className="text-danger">*</span>{" "}
                              {t("Display Order")}
                            </label>
                            <Field
                              type="text"
                              id="displayOrder"
                              name="displayOrder"
                              placeholder={t("Display Order")}
                              className={`form-control ${
                                touched.displayOrder && errors.displayOrder
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="displayOrder" />
                          </div>
                        </div>

                        <div className="col-12 d-flex justify-content-end mt-5 mb-4">
                          <Button
                            type="button"
                            className="mx-2"
                            color="secondary"
                            onClick={() => navigate(-1)}
                          >
                            {t("Cancel")}
                          </Button>
                          <Button
                            type="submit"
                            color="success"
                            disabled={isLoading}
                            className="w_fit_content"
                          >
                            {mode === "add" ? t("Add") : t("Save")}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default AddCity
